import { Link } from "react-router-dom";
import { FileBarChart2, ExternalLink, Trash2, Loader2, Download, Copy } from "lucide-react";
import type { Report } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

interface ReportCardProps {
  report: Report;
  onDelete: () => void;
  onUseTemplate: () => void;
}

export function ReportCard({ report, onDelete, onUseTemplate }: ReportCardProps) {
  const totalCalls = report.models.length * report.runs_per_model;
  const shareUrl = `/reports/${report.slug}`;
  const appUrl = `/app/reports/${report.id}`;
  const brandSummary = report.tracked_brand_source === "workspace_all" && report.brand_ids.length === 0
    ? "All workspace brands"
    : `${report.brand_ids.length} brand${report.brand_ids.length === 1 ? "" : "s"}`;

  const statusBadge = report.status === "running"
    ? <Badge variant="muted" className="gap-1"><Loader2 className="w-3 h-3 animate-spin" />Generating</Badge>
    : report.status === "failed"
      ? <Badge variant="muted" className="bg-red-50 text-red-700 border-red-100">Failed</Badge>
      : <Badge variant="muted" className="bg-emerald-50 text-emerald-700 border-emerald-100">Ready</Badge>;

  return (
    <Card className="hover:border-brand-200 transition-colors">
      <CardContent className="py-5 px-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-md bg-brand-50 border border-brand-100 flex items-center justify-center shrink-0">
              <FileBarChart2 className="w-4 h-4 text-brand-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-[14px] font-semibold text-brand-950 truncate">
                <Link to={appUrl} className="hover:text-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm">
                  {report.name}
                </Link>
              </h3>
              <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                {brandSummary} · {report.models.length} model{report.models.length === 1 ? '' : 's'} · {totalCalls} total call{totalCalls === 1 ? '' : 's'}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            {statusBadge}
            {report.query_mode === "blind_query" && <Badge>Blind Query</Badge>}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 mt-3">
          <p className="text-[12px] text-slate-400">
            Created {new Date(report.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </p>
          <div className="flex items-center gap-1">
            <Link
              to={appUrl}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-brand-600 hover:text-brand-700 px-2 py-1 rounded hover:bg-brand-50 transition-colors"
            >
              Results
            </Link>
            <button
              type="button"
              onClick={onUseTemplate}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-500 hover:text-brand-700 px-2 py-1 rounded hover:bg-brand-50 transition-colors"
            >
              <Copy className="w-3 h-3" />
              Use as template
            </button>
            <a
              href={shareUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-500 hover:text-brand-700 px-2 py-1 rounded hover:bg-brand-50 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              Public
            </a>
            {report.status === "completed" && (
              <a
                href={`${shareUrl}/export.docx`}
                className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-500 hover:text-brand-700 px-2 py-1 rounded hover:bg-brand-50 transition-colors"
                title="Download as Word document"
              >
                <Download className="w-3 h-3" />
                .docx
              </a>
            )}
            <button
              onClick={onDelete}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-400 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50 transition-colors"
              aria-label="Delete report"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
